import { ChangePasswordScreen } from '@/features/auth/ChangePasswordScreen';
import { Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useSession } from '@/features/auth/session';
import { NotificationsPanel } from '@/features/notifications/NotificationsPanel';
import { ReportBugButton } from '@/features/pm/bug-report/ReportBugButton';
import { cn } from '@/lib/cn';
import { pmContext } from '@/lib/client-errors';
import { LoadingState } from '@/ui/States';
import { CrashBoundary } from './CrashBoundary';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

export function AppLayout() {
  const { data: session, isPending, isFetching } = useSession();
  const location = useLocation();

  // A cleared cache reports "pending" with no fetch in flight; treat that as
  // signed out so logout lands immediately instead of showing a spinner. A
  // failed refetch is not a sign-out: the session it had stays, and a 401
  // arrives as null.
  if (!session && !isFetching) return <Navigate to="/login" replace />;
  if (isPending || !session) return <LoadingState />;

  // Not a redirect: a route can be navigated around, and the server refuses
  // every other endpoint anyway. The application simply is not there yet.
  if (session.mustChangePassword) return <ChangePasswordScreen />;

  const reportsBugs = pmContext(location.pathname).isPm && session.permissions.includes('pm.workspace.view');

  return (
    <div className="fixed inset-0 flex overflow-hidden">
      {/* First, so the sidebar's mobile backdrop and every dialog sit above it. */}
      {reportsBugs && <ReportBugButton />}
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar />
        {/* Room at the bottom so the last row can scroll clear of the bug button. */}
        <main className={cn('flex-1 overflow-y-auto overscroll-contain bg-canvas px-6 py-6', reportsBugs && 'pb-20')}>
          <CrashBoundary key={location.pathname}>
            <Suspense fallback={<LoadingState />}>
              <Outlet />
            </Suspense>
          </CrashBoundary>
        </main>
      </div>
      <NotificationsPanel />
    </div>
  );
}
